/**
 * Starter RangeControl — a controlled, presentational multi-range filter for
 * ONE portfolio field, used by the Segment Builder's filter rail.
 *
 * Each row is either a RANGE (dual-handle Mantine `RangeSlider` bounded to the
 * field's portfolio `[bounds.min, bounds.max]`, paired with two `NumberInput`s
 * in numeric mode) or an EXACT value (one input; emitted as the degenerate range
 * `{min: v, max: v}`, so the wire shape never changes). A per-row Range/Exact
 * toggle switches modes; dragging or typing one bound past the other clamps the
 * pair (the values swap-free; the paired bound moves), so the control never
 * errors. Multiple rows express OR — "Add range" appends one, each row carries a
 * remove (×) control — to carve out, say, 2008 and COVID, or exactly 60- and
 * 120-month terms. An "Include missing" checkbox covers loans with no value.
 *
 * DATE mode (`mode: 'date'`) treats every bound/value as a YYYYMMDD integer
 * ordinal (e.g. 20210315). A range shows BOTH controls at once (mirroring the
 * numeric field): a month-snapping `RangeSlider` above — the ordinal domain is
 * mapped onto a dense month axis (see month-snap.ts), the min handle pinning to
 * the first day of its month and the max handle to the last — and, below it,
 * typed `DateField`s (React Aria date segments) for From/To, stacked so two full
 * dates always fit the ~320px rail. Each date field carries a calendar button
 * that opens a `@mantine/dates` `MonthPicker` (opening at the decade level); a
 * picked month maps to the boundary day (min to first, max to last). Dragging the
 * slider updates the fields live; the wire value stays YYYYMMDD ordinals
 * throughout. An exact date drops the slider for a single field.
 *
 * Live-drag vs. commit: slider handles emit continuously while dragging (kept in
 * local `dragRows` state for smooth visual feedback) but only COMMIT upward on
 * release via `onChangeEnd` — no per-tick store write / localStorage thrash.
 *
 * EMBEDDED (`embedded`): the SegmentPanel wraps this in its own full-bleed white
 * body, so `embedded` drops the control's own card chrome (border/bg/shadow/
 * padding) and suppresses the internal legend (the FieldRow header already names
 * the field — no duplicate). Standalone stories leave it off to keep the surface.
 *
 * Controlled: it renders from props and emits the full next value via
 * `onChange`. An empty `ranges` array renders one empty editing row, but only
 * non-empty edits are emitted.
 */
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import {
  Checkbox,
  Group,
  NumberInput,
  Popover,
  RangeSlider,
  Stack,
  Text,
} from '@mantine/core';
import { MonthPicker } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, X } from '~/components/icons';
import { ActionIcon } from './ActionIcon';
import { Button } from './Button';
import { DateField } from './DateField';
import { SegmentedControl } from './SegmentedControl';
import {
  monthIndexToEndOrdinal,
  monthIndexToStartOrdinal,
  ordinalToMonthIndex,
} from './month-snap';
import styles from './RangeControl.module.css';

export interface SegmentRangeValue {
  readonly min: number | null;
  readonly max: number | null;
}

export interface RangeControlProps {
  readonly label: string;
  readonly mode: 'numeric' | 'date';
  /** Portfolio min/max for this field (fixed slider bounds). */
  readonly bounds: { min: number; max: number };
  readonly ranges: readonly SegmentRangeValue[];
  /** Include loans with no value for this field. */
  readonly includeMissing: boolean;
  readonly onChange: (next: {
    ranges: SegmentRangeValue[];
    includeMissing: boolean;
  }) => void;
  /** Optional suffix label, e.g. '%' or '$'. */
  readonly unit?: string;
  readonly disabled?: boolean;
  /**
   * Embedded in a host surface (the SegmentPanel's white body): drop this
   * control's own card chrome + the internal legend so it doesn't nest a second
   * card / repeat the field name. Standalone (stories) leave it off.
   */
  readonly embedded?: boolean;
}

/** The single empty row shown for editing when the caller passes no ranges. */
const EMPTY_ROW: SegmentRangeValue = { min: null, max: null };

/** Value-equality for two range lists (identity-independent) — the prop-sync guard. */
function rangesShallowEqual(
  a: readonly SegmentRangeValue[],
  b: readonly SegmentRangeValue[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i]?.min !== b[i]?.min || a[i]?.max !== b[i]?.max) return false;
  }
  return true;
}

/**
 * CSS-Module class lookups are typed `string | undefined` under the kit's
 * strict config; coalesce to a concrete string so they satisfy Mantine's
 * `className` (which is non-optional under `exactOptionalPropertyTypes`).
 */
function cx(...classes: (string | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Clamp a number into the portfolio bounds. */
function clampToBounds(value: number, bounds: { min: number; max: number }): number {
  return Math.min(bounds.max, Math.max(bounds.min, value));
}

/**
 * YYYYMMDD integer ordinal → Mantine's `'YYYY-MM-DD'` date string (or null).
 * Mantine v9 `@mantine/dates` speaks string dates, not `Date` objects.
 */
function ordinalToDateString(ordinal: number | null): string | null {
  if (ordinal === null) return null;
  const yyyy = Math.floor(ordinal / 10000);
  const mm = Math.floor((ordinal % 10000) / 100);
  const dd = ordinal % 100;
  const pad = (n: number, width: number): string => String(n).padStart(width, '0');
  return `${pad(yyyy, 4)}-${pad(mm, 2)}-${pad(dd, 2)}`;
}

/** Mantine's `'YYYY-MM-DD'` date string → YYYYMMDD integer ordinal (or null). */
function dateStringToOrdinal(value: string | null): number | null {
  if (value === null || value === '') return null;
  const parts = value.split('-').map((p) => Number.parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [yyyy, mm, dd] = parts as [number, number, number];
  return yyyy * 10000 + mm * 100 + dd;
}

/** YYYYMMDD ordinal → a `'YYYY-MM-01'` month string for the Mantine MonthPicker. */
function ordinalToMonthString(ordinal: number | null): string | null {
  if (ordinal === null) return null;
  const yyyy = Math.floor(ordinal / 10000);
  const mm = Math.floor((ordinal % 10000) / 100);
  const pad = (n: number, width: number): string => String(n).padStart(width, '0');
  return `${pad(yyyy, 4)}-${pad(mm, 2)}-01`;
}

/** A MonthPicker's picked month (`'YYYY-MM-DD'`) → the boundary ordinal, reusing the
 *  shared month axis so `min` lands on the first day of the month and `max` on the
 *  last (leap months included). */
function monthStringToOrdinal(
  value: string | null,
  boundary: 'min' | 'max',
): number | null {
  if (value === null || value === '') return null;
  const parts = value.split('-').map((p) => Number.parseInt(p, 10));
  const yyyy = parts[0];
  const mm = parts[1];
  if (yyyy === undefined || mm === undefined || Number.isNaN(yyyy) || Number.isNaN(mm)) {
    return null;
  }
  const monthIndex = yyyy * 12 + (mm - 1);
  return boundary === 'max'
    ? monthIndexToEndOrdinal(monthIndex)
    : monthIndexToStartOrdinal(monthIndex);
}

/** Today as an ISO `'YYYY-MM-DD'` string — the date picker's max (no future dates). */
function todayDateString(): string {
  const now = new Date();
  const pad = (n: number, width: number): string => String(n).padStart(width, '0');
  return `${pad(now.getFullYear(), 4)}-${pad(now.getMonth() + 1, 2)}-${pad(now.getDate(), 2)}`;
}

/** A stored row reads as EXACT when both bounds are present and equal — the
 *  degenerate range an exact-value row emits. */
function isExactRow(row: SegmentRangeValue): boolean {
  return row.min !== null && row.min === row.max;
}

interface DateBoundInputProps {
  /** Which end this field is — decides how a picked month maps to a day. */
  readonly boundary: 'min' | 'max';
  readonly valueOrdinal: number | null;
  readonly onChangeOrdinal: (ordinal: number | null) => void;
  readonly ariaLabel: string;
  readonly label?: string | undefined;
  readonly disabled: boolean;
}

/**
 * One date bound: a typed React Aria `DateField` plus a calendar button that opens
 * a `MonthPicker` (decade level). Speaks YYYYMMDD ordinals at its edge; a picked
 * month maps to the boundary day via {@link monthStringToOrdinal} (min → first,
 * max → last), while typing allows an exact day.
 */
function DateBoundInput({
  boundary,
  valueOrdinal,
  onChangeOrdinal,
  ariaLabel,
  label,
  disabled,
}: DateBoundInputProps): ReactElement {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  // No minimum (any past date is valid); cap the max at TODAY — a future date never
  // makes sense for a portfolio date. The picker opens on the most recent decade.
  const today = todayDateString();
  const todayMonth = `${today.slice(0, 7)}-01`;
  const selectedMonth = ordinalToMonthString(valueOrdinal);
  return (
    <DateField
      value={ordinalToDateString(valueOrdinal)}
      onChange={(v) => onChangeOrdinal(dateStringToOrdinal(v))}
      isDisabled={disabled}
      aria-label={ariaLabel}
      maxValue={today}
      {...(label !== undefined ? { label } : {})}
      trailing={
        <Popover
          opened={pickerOpen}
          onChange={setPickerOpen}
          position="bottom-end"
          withinPortal
          trapFocus
          shadow="md"
        >
          <Popover.Target>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label={t('segment.range.openCalendar', { label: ariaLabel })}
              disabled={disabled}
              onClick={() => setPickerOpen((open) => !open)}
            >
              <Calendar size={15} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown p={6}>
            <MonthPicker
              size="xs"
              defaultLevel="decade"
              value={selectedMonth}
              defaultDate={selectedMonth ?? todayMonth}
              maxDate={todayMonth}
              onChange={(picked) => {
                const ordinal = monthStringToOrdinal(picked, boundary);
                if (ordinal !== null) onChangeOrdinal(ordinal);
                setPickerOpen(false);
              }}
            />
          </Popover.Dropdown>
        </Popover>
      }
    />
  );
}

export function RangeControl({
  label,
  mode,
  bounds,
  ranges,
  includeMissing,
  onChange,
  unit,
  disabled = false,
  embedded = false,
}: RangeControlProps): ReactElement {
  const { t } = useTranslation();
  const baseId = useId();
  const isDate = mode === 'date';

  // Value CLAMP bounds. Numeric clamps typed values to the portfolio range. DATE clamps
  // only to "no lower bound, max = today" (matching the calendar) — the SLIDER is still
  // bounded to the data range, but typing or the month picker may land on any past date
  // up to today, and clamping those back to the portfolio range would REVERT the user's
  // input (typing a year before the data starts snapped straight back to the floor).
  const clampBounds = isDate
    ? { min: 0, max: dateStringToOrdinal(todayDateString()) ?? bounds.max }
    : bounds;

  // The control owns its EDITING rows locally so a freshly "Add range"d (empty) row
  // stays on screen to be filled in. Only non-empty rows are emitted upward (the
  // stored filter never carries a blank range) — but stripping the blanks on emit
  // must NOT erase the in-progress row from the display, which is exactly what made
  // "Add range" appear to do nothing when the control was driven purely off the
  // cleaned `ranges` prop. We render `rows`; we emit the cleaned subset.
  const [rows, setRows] = useState<SegmentRangeValue[]>(() =>
    ranges.length > 0 ? [...ranges] : [EMPTY_ROW],
  );
  // Per-row RANGE vs EXACT display mode, parallel to `rows`. Local UI state (the wire
  // shape stays ranges: an exact row is the degenerate {min: v, max: v}); initialized
  // from the data so a saved exact row reopens in exact mode.
  const [exactFlags, setExactFlags] = useState<boolean[]>(() =>
    (ranges.length > 0 ? ranges : [EMPTY_ROW]).map(isExactRow),
  );
  // Live slider positions during a drag, keyed by row index. Present only while a
  // handle is being dragged (so we render the moving handle) — cleared on release,
  // when the committed `rows`/`ranges` take over. This is the "smooth feedback,
  // commit-on-release" seam that keeps the store off the per-tick drag path.
  const [dragRows, setDragRows] = useState<Record<number, [number, number]>>({});
  // The cleaned ranges we last emitted, so the sync effect can tell our own
  // round-trip (keep the editing rows, incl. a trailing empty one) apart from an
  // external change (reset / hydrate a saved segment) that should replace them.
  const lastEmittedRef = useRef<readonly SegmentRangeValue[]>(ranges);
  useEffect(() => {
    if (rangesShallowEqual(ranges, lastEmittedRef.current)) return;
    lastEmittedRef.current = ranges;
    const next = ranges.length > 0 ? [...ranges] : [EMPTY_ROW];
    setRows(next);
    setExactFlags(next.map(isExactRow));
    setDragRows({});
  }, [ranges]);

  /** Update the visible rows (and their modes), then emit the non-empty subset. */
  function emit(
    nextRows: SegmentRangeValue[],
    nextExactFlags: boolean[],
    nextIncludeMissing: boolean,
  ): void {
    const displayRows = nextRows.length > 0 ? nextRows : [EMPTY_ROW];
    setRows(displayRows);
    setExactFlags(nextRows.length > 0 ? nextExactFlags : [false]);
    const cleaned = nextRows.filter((r) => r.min !== null || r.max !== null);
    lastEmittedRef.current = cleaned;
    onChange({ ranges: cleaned, includeMissing: nextIncludeMissing });
  }

  /** Replace row `index` with `next`, then emit. */
  function setRow(index: number, next: SegmentRangeValue): void {
    const draft = rows.map((r, i) => (i === index ? next : r));
    emit(draft, exactFlags, includeMissing);
  }

  function setMin(index: number, rawMin: number | null): void {
    const row = rows[index];
    if (!row) return;
    let min = rawMin;
    let max = row.max;
    if (min !== null) {
      min = clampToBounds(min, clampBounds);
      // Typing min past max moves the paired bound (clamp), never errors.
      if (max !== null && min > max) max = min;
    }
    setRow(index, { min, max });
  }

  function setMax(index: number, rawMax: number | null): void {
    const row = rows[index];
    if (!row) return;
    let min = row.min;
    let max = rawMax;
    if (max !== null) {
      max = clampToBounds(max, clampBounds);
      if (min !== null && max < min) min = max;
    }
    setRow(index, { min, max });
  }

  /** Exact mode: one value, emitted as the degenerate range {min: v, max: v}. */
  function setExactValue(index: number, raw: number | null): void {
    const value = raw === null ? null : clampToBounds(raw, clampBounds);
    setRow(index, { min: value, max: value });
  }

  /** Slider always emits a concrete [min, max] within bounds — commit on release. */
  function commitFromSlider(index: number, [min, max]: [number, number]): void {
    setDragRows((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setRow(index, { min, max });
  }

  /** Live drag feedback only — held locally, NOT emitted upward. */
  function dragSlider(index: number, value: [number, number]): void {
    setDragRows((prev) => ({ ...prev, [index]: value }));
  }

  /** Month-snap slider: the domain is a dense month axis; commit ordinals on release
   *  (min → first-of-month, max → last-of-month) so the range spans whole months. */
  function commitFromMonthSlider(index: number, [minIdx, maxIdx]: [number, number]): void {
    setDragRows((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setRow(index, {
      min: monthIndexToStartOrdinal(minIdx),
      max: monthIndexToEndOrdinal(maxIdx),
    });
  }

  /** Flip one row between range and exact editing. Exact keeps the row's min (or max)
   *  as the single value; back to range keeps the degenerate pair to widen from. */
  function setRowExact(index: number, exact: boolean): void {
    const row = rows[index];
    if (!row) return;
    const nextFlags = exactFlags.map((f, i) => (i === index ? exact : f));
    if (exact) {
      const value = row.min ?? row.max;
      const draft = rows.map((r, i) =>
        i === index ? { min: value, max: value } : r,
      );
      emit(draft, nextFlags, includeMissing);
      return;
    }
    setExactFlags(nextFlags);
  }

  function addRange(): void {
    emit([...rows, EMPTY_ROW], [...exactFlags, false], includeMissing);
  }

  function removeRange(index: number): void {
    const draft = rows.filter((_, i) => i !== index);
    const draftFlags = exactFlags.filter((_, i) => i !== index);
    // Keep one editable row on screen even after removing the last range.
    emit(
      draft.length > 0 ? draft : [EMPTY_ROW],
      draft.length > 0 ? draftFlags : [false],
      includeMissing,
    );
  }

  // A range with no explicit bound falls back to the portfolio edge for the
  // slider handle position (the slider can't represent null).
  function sliderValue(row: SegmentRangeValue): [number, number] {
    return [row.min ?? bounds.min, row.max ?? bounds.max];
  }

  // Month-axis bounds for the date slider (whole months spanned by the portfolio).
  const monthBounds = {
    min: ordinalToMonthIndex(bounds.min),
    max: ordinalToMonthIndex(bounds.max),
  };

  return (
    <fieldset
      className={cx(styles.root, embedded ? styles.embedded : undefined)}
      disabled={disabled}
    >
      {embedded ? null : (
        <legend className={cx(styles.legend)}>
          {label}
          {unit ? <span className={cx(styles.unit)}> ({unit})</span> : null}
        </legend>
      )}

      <Stack gap="sm">
        {rows.map((row, index) => {
          const [sliderMin, sliderMax] = sliderValue(row);
          const exact = exactFlags[index] === true;
          const minId = `${baseId}-min-${index}`;
          const maxId = `${baseId}-max-${index}`;
          const exactId = `${baseId}-exact-${index}`;
          // Conditionally spread the unit suffix — omit it (rather than pass
          // `undefined`) so it satisfies `exactOptionalPropertyTypes`.
          const suffixProp = unit ? { suffix: ` ${unit}` } : {};
          const rowIndexArgs = { label, index: index + 1 };
          // A date range shows the month slider above the typed From/To fields; an
          // exact date (like an exact number) drops the slider for a single field.
          const useMonthSlider = isDate && !exact;
          return (
            <div className={cx(styles.row)} key={index}>
              <div className={cx(styles.rowHeader)}>
                <Text className={cx(styles.rowLabel)}>
                  {rows.length > 1
                    ? t('segment.range.rowN', { index: index + 1 })
                    : t('segment.range.row')}
                </Text>
                {/* Range vs exact — a compact toggle, for both numeric and date. */}
                <SegmentedControl
                  classNames={{
                    root: cx(styles.modeToggle),
                    label: cx(styles.modeToggleLabel),
                  }}
                  size="xs"
                  value={exact ? 'exact' : 'range'}
                  onChange={(next) => setRowExact(index, next === 'exact')}
                  disabled={disabled}
                  aria-label={t('segment.range.modeLabel', rowIndexArgs)}
                  data={[
                    { value: 'range', label: t('segment.range.modeRange') },
                    { value: 'exact', label: t('segment.range.modeExact') },
                  ]}
                />
                {rows.length > 1 ? (
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    aria-label={t('segment.range.removeRow', { index: index + 1 })}
                    onClick={() => removeRange(index)}
                    disabled={disabled}
                  >
                    <X size={14} />
                  </ActionIcon>
                ) : null}
              </div>

              {useMonthSlider ? (
                // DATE RANGE: a month-snap slider (no tooltip) above, typed From/To
                // fields below — both live at once, mirroring the numeric field.
                (() => {
                  const committed: [number, number] = [
                    ordinalToMonthIndex(row.min ?? bounds.min),
                    ordinalToMonthIndex(row.max ?? bounds.max),
                  ];
                  const drag = dragRows[index];
                  const [dragMin, dragMax] = drag ?? committed;
                  // While dragging, the fields track the live slider month; otherwise
                  // they show the committed bound (null → placeholder segments).
                  const fromOrdinal =
                    drag !== undefined ? monthIndexToStartOrdinal(dragMin) : row.min;
                  const toOrdinal =
                    drag !== undefined ? monthIndexToEndOrdinal(dragMax) : row.max;
                  return (
                    <>
                      <RangeSlider
                        className={cx(styles.slider)}
                        min={monthBounds.min}
                        max={monthBounds.max}
                        step={1}
                        minRange={0}
                        value={[dragMin, dragMax]}
                        onChange={(v) => dragSlider(index, v)}
                        onChangeEnd={(v) => commitFromMonthSlider(index, v)}
                        disabled={disabled}
                        label={null}
                        aria-label={t('segment.range.sliderLabel', rowIndexArgs)}
                      />
                      <Stack gap="xs" className={cx(styles.inputs)}>
                        <DateBoundInput
                          boundary="min"
                          label={t('segment.range.from')}
                          ariaLabel={t('segment.range.minimum', { label })}
                          valueOrdinal={fromOrdinal}
                          onChangeOrdinal={(o) => setMin(index, o)}
                          disabled={disabled}
                        />
                        <DateBoundInput
                          boundary="max"
                          label={t('segment.range.to')}
                          ariaLabel={t('segment.range.maximum', { label })}
                          valueOrdinal={toOrdinal}
                          onChangeOrdinal={(o) => setMax(index, o)}
                          disabled={disabled}
                        />
                      </Stack>
                    </>
                  );
                })()
              ) : isDate ? (
                // DATE EXACT: a single typed field + month picker, no slider.
                <Stack gap="xs" className={cx(styles.inputs)}>
                  <DateBoundInput
                    boundary="min"
                    ariaLabel={t('segment.range.exactValue', { label })}
                    valueOrdinal={row.min}
                    onChangeOrdinal={(o) => setExactValue(index, o)}
                    disabled={disabled}
                  />
                </Stack>
              ) : exact ? (
                // NUMERIC EXACT: one value; emitted as {min: v, max: v}.
                <Group gap="xs" wrap="nowrap" className={cx(styles.inputs)}>
                  <NumberInput
                    id={exactId}
                    className={cx(styles.numberInput)}
                    aria-label={t('segment.range.exactValue', { label })}
                    value={row.min ?? ''}
                    min={bounds.min}
                    max={bounds.max}
                    {...suffixProp}
                    disabled={disabled}
                    hideControls
                    onChange={(v) =>
                      setExactValue(index, v === '' || v === null ? null : Number(v))
                    }
                  />
                </Group>
              ) : (
                // NUMERIC RANGE: dual-handle slider (commit on release) + input pair.
                <>
                  <RangeSlider
                    className={cx(styles.slider)}
                    min={bounds.min}
                    max={bounds.max}
                    value={dragRows[index] ?? [sliderMin, sliderMax]}
                    onChange={(v) => dragSlider(index, v)}
                    onChangeEnd={(v) => commitFromSlider(index, v)}
                    disabled={disabled}
                    label={null}
                    aria-label={t('segment.range.sliderLabel', rowIndexArgs)}
                  />

                  <Group gap="xs" wrap="nowrap" className={cx(styles.inputs)}>
                    <NumberInput
                      id={minId}
                      className={cx(styles.numberInput)}
                      aria-label={t('segment.range.minimum', { label })}
                      // While dragging show the LIVE slider bound so the reader can see where
                      // they are before releasing; the committed value takes over on release.
                      value={dragRows[index]?.[0] ?? row.min ?? ''}
                      min={bounds.min}
                      max={bounds.max}
                      {...suffixProp}
                      disabled={disabled}
                      hideControls
                      onChange={(v) =>
                        setMin(index, v === '' || v === null ? null : Number(v))
                      }
                    />
                    <Text className={cx(styles.dash)} aria-hidden>
                      –
                    </Text>
                    <NumberInput
                      id={maxId}
                      className={cx(styles.numberInput)}
                      aria-label={t('segment.range.maximum', { label })}
                      value={dragRows[index]?.[1] ?? row.max ?? ''}
                      min={bounds.min}
                      max={bounds.max}
                      {...suffixProp}
                      disabled={disabled}
                      hideControls
                      onChange={(v) =>
                        setMax(index, v === '' || v === null ? null : Number(v))
                      }
                    />
                  </Group>
                </>
              )}
            </div>
          );
        })}

        <div className={cx(styles.footer)}>
          <Button
            variant="ghost"
            size="xs"
            leftSection={<Plus size={14} />}
            onClick={addRange}
            disabled={disabled}
          >
            {t('segment.range.addRange')}
          </Button>

          <Checkbox
            className={cx(styles.includeMissing)}
            size="sm"
            label={t('segment.range.includeMissing')}
            checked={includeMissing}
            disabled={disabled}
            onChange={(e) => emit([...rows], [...exactFlags], e.currentTarget.checked)}
          />
        </div>
      </Stack>
    </fieldset>
  );
}
